import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { ErrorBox, Panel } from '../components/ui'
import { api, ApiError } from '../lib/api'
import type { Booking, EnquiryDetail, Room, Service } from '../types'

export function BookingNew() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const enquiryId = params.get('enquiry_id')

  const rooms = useQuery({ queryKey: ['rooms'], queryFn: () => api.get<Room[]>('/rooms') })
  const services = useQuery({ queryKey: ['services'], queryFn: () => api.get<Service[]>('/services') })
  const enquiry = useQuery({
    queryKey: ['enquiry', enquiryId],
    queryFn: () => api.get<EnquiryDetail>(`/enquiries/${enquiryId}`),
    enabled: Boolean(enquiryId),
  })

  const defaultDay = params.get('day') ?? (() => {
    const d = new Date()
    d.setDate(d.getDate() + 3)
    return d.toISOString().slice(0, 10)
  })()

  const [form, setForm] = useState({
    room_id: 0, service_code: 'music', day: defaultDay, start_time: '10:00',
    hours: 6, name: '', email: '', engineer: 'Luis', notes: '',
  })

  const linked = enquiry.data?.enquiry
  const field = (key: keyof typeof form) => ({
    value: String(form[key] ?? ''),
    onChange: (event: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [key]: key === 'hours' ? Number(event.target.value) : event.target.value })),
  })

  const create = useMutation({
    mutationFn: () =>
      api.post<Booking>('/bookings', {
        ...form,
        room_id: form.room_id || rooms.data?.[0]?.id,
        name: form.name || linked?.name || '',
        email: form.email || linked?.email || '',
        enquiry_id: enquiryId ? Number(enquiryId) : null,
      }),
    onSuccess: (booking) => navigate(`/bookings/${booking.id}`),
  })

  const conflict = create.error instanceof ApiError && create.error.status === 409

  return (
    <>
      <PageBar title="New booking" actions={<Link className="btn ghost" to="/calendar">Cancel</Link>} />
      <Content>
        {create.error && (conflict
          ? <div className="alarm">{(create.error as ApiError).message}</div>
          : <ErrorBox error={create.error} />)}

        <Panel pad className="form">
          {linked && (
            <div className="from-enquiry">
              From enquiry: <Link to={`/enquiries/${linked.id}`}>{linked.name}</Link> —{' '}
              {linked.message.slice(0, 110)}…
            </div>
          )}

          <div className="grid2">
            <label>Client name
              <input type="text" {...field('name')} placeholder={linked?.name ?? ''} />
            </label>
            <label>Email
              <input type="email" {...field('email')} placeholder={linked?.email ?? ''} />
            </label>
          </div>

          <div className="grid3">
            <label>Room
              <select {...field('room_id')}>
                {rooms.data?.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
            <label>Service
              <select {...field('service_code')}>
                {services.data?.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </label>
            <label>Engineer<input type="text" {...field('engineer')} /></label>
          </div>

          <div className="grid3">
            <label>Date<input type="date" {...field('day')} required /></label>
            <label>Start<input type="time" {...field('start_time')} required /></label>
            <label>Hours
              <input type="number" min={1} max={14} step={0.5} {...field('hours')} required />
            </label>
          </div>

          <label>Notes<textarea rows={3} {...field('notes')} /></label>

          <div className="form-foot">
            <button className="btn" type="button" disabled={create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Create booking'}
            </button>
            <span className="hint">
              Price comes from the rate card — the day rate applies automatically once it beats hourly.
            </span>
          </div>
        </Panel>
      </Content>
    </>
  )
}
